import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Encounter, LabRequest, RadiologyRequest } from '../../types';
import { Search, FileText, FlaskConical, Scan, Printer, Download, X } from 'lucide-react';
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest, isAMHTest } from '../../store/labStructures';
import jsPDF from 'jspdf';
/** Short, stable report number derived from the request id. */
const reportNumber = (prefix: 'LAB' | 'RAD', id: string) => `${prefix}-${id.replace(/[^a-zA-Z0-9]/g, '').slice(-6).toUpperCase()}`;
interface Props {}
export const PatientReports: React.FC<Props> = () => {
  const {
    encounters,
    patients
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewing, setViewing] = useState<{
    encounter: Encounter;
  } | null>(null);
  // Encounters that have at least one completed lab OR a radiology report
  const reportEncounters = useMemo(() => {
    return encounters.filter(e => {
      const hasCompletedLab = e.labRequests.some(l => l.status === 'Completed');
      const hasRadiologyReport = e.radiologyRequests.some(r => r.report || r.images && r.images.length > 0);
      return hasCompletedLab || hasRadiologyReport;
    }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [encounters]);
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reportEncounters;
    return reportEncounters.filter(e => {
      const p = patients.find(pt => pt.id === e.patientId);
      if (!p) return false;
      const reportNumbers = [...e.labRequests.filter(l => l.status === 'Completed').map(l => reportNumber('LAB', l.id)), ...e.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0).map(r => reportNumber('RAD', r.id))];
      return `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q) || reportNumbers.some(n => n.toLowerCase().includes(q));
    });
  }, [reportEncounters, patients, searchQuery]);
  const getPatient = (id: string) => patients.find(p => p.id === id);
  const handlePrint = () => window.print();
  const handleDownloadPDF = () => {
    if (!viewing) return;
    const patient = getPatient(viewing.encounter.patientId);
    if (!patient) return;
    const pdf = new jsPDF({
      unit: 'pt',
      format: 'a4'
    });
    const margin = 40;
    let y = margin;
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Patient Report', margin, y);
    y += 22;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${patient.firstName} ${patient.lastName} · BMC ${patient.bmcFileNumber} · ${patient.age} yrs · ${patient.sex}`, margin, y);
    y += 14;
    pdf.text(`Visit date: ${new Date(viewing.encounter.startedAt).toLocaleString()}`, margin, y);
    y += 20;
    const lineCheck = () => {
      const ph = pdf.internal.pageSize.getHeight() - margin;
      if (y > ph) {
        pdf.addPage();
        y = margin;
      }
    };
    // ── Laboratory results ──
    const completedLabs = viewing.encounter.labRequests.filter(l => l.status === 'Completed');
    if (completedLabs.length > 0) {
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Laboratory Results', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      completedLabs.forEach(lab => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
        pdf.text(`Tests: ${activeTests.join(', ')}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (lab.completedAt) {
          pdf.text(`Completed: ${new Date(lab.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        if (lab.results) {
          Object.entries(lab.results).forEach(([k, v]) => {
            if (!v) return;
            lineCheck();
            const line = `  ${k}: ${v}`;
            const lines = pdf.splitTextToSize(line, 515);
            pdf.text(lines, margin, y);
            y += lines.length * 11;
          });
        }
        if (lab.otherTests) {
          lineCheck();
          pdf.text(`  Other: ${lab.otherTests}`, margin, y);
          y += 12;
        }
        if (lab.labComments) {
          lineCheck();
          pdf.setFont('helvetica', 'bold');
          pdf.text('Comments / Observations:', margin, y);
          y += 12;
          pdf.setFont('helvetica', 'normal');
          const lines = pdf.splitTextToSize(lab.labComments, 515);
          lines.forEach((line: string) => {
            lineCheck();
            pdf.text(line, margin, y);
            y += 11;
          });
        }
        y += 6;
      });
    }
    // ── Radiology results ──
    const radWithReport = viewing.encounter.radiologyRequests.filter(r => r.report);
    if (radWithReport.length > 0) {
      lineCheck();
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Radiology Reports', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      radWithReport.forEach(r => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Scan: ${r.scanType}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (r.completedAt) {
          pdf.text(`Completed: ${new Date(r.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        const lines = pdf.splitTextToSize(r.report || '', 515);
        lines.forEach((line: string) => {
          lineCheck();
          pdf.text(line, margin, y);
          y += 11;
        });
        y += 8;
      });
    }
    pdf.save(`report-${patient.bmcFileNumber}-${new Date(viewing.encounter.startedAt).toISOString().slice(0, 10)}.pdf`);
  };
  // ── Renderers for the View modal ──
  const renderLabResults = (lab: LabRequest) => {
    if (!lab.results) return null;
    const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
    const focusedTest = activeTests[0];
    if (isFBCTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5880" data-mp="2thnc3tvp6sw6">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5881" data-mp="2thduouopxu23">
            FBC Report
          </h4>
          {fbcStructure.map(cat => <div key={cat.category} className="mb-3" data-id="element-5882" data-mp="18xrzyg3jdryq">
              <p className="font-semibold text-gray-700 mb-1" data-id="element-5883" data-mp="1dzhck06eqdox">{cat.category}</p>
              <table className="w-full text-xs border border-gray-300" data-id="element-5884" data-mp="1i017qy7f778f">
                <thead className="bg-gray-100" data-id="element-5885" data-mp="1mqzrk7rmangf">
                  <tr data-id="element-5886" data-mp="1mrt0nfe1tp1q">
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-5887" data-mp="1mt44ff7huwsu">
                      Test
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-24" data-id="element-5888" data-mp="1woxduppx2a6e">
                      Result
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-5889" data-mp="21pbomfiwjy84">
                      Reference
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-20" data-id="element-5890" data-mp="25nrin3dei6v1">
                      Unit
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-5891" data-mp="bcmablrdd69d">
                  {cat.tests.map(t => <tr key={t.name} data-id="element-5892" data-mp="3okgmbygeuf96">
                      <td className="border border-gray-300 p-1.5" data-id="element-5893" data-mp="3ojf49a0z8shy">{t.name}</td>
                      <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5894" data-mp="3jl3xx99w504h">
                        {lab.results![t.name] || '—'}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5895" data-mp="3tbykyfuyo3qz">
                        {t.range}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5896" data-mp="35n5o8h6ilrvb">
                        {t.unit}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>)}
        </div>;
    }
    if (isUrinalysisTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5897" data-mp="39q28sdfm7y1w">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5898" data-mp="tw1vuaeem58o">
            Urinalysis Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5899" data-mp="owozmz1n7ltf">
            <thead className="bg-gray-100" data-id="element-5900" data-mp="xdzfzz7y7kq2">
              <tr data-id="element-5901" data-mp="xcxu0zpe9dtk">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5902" data-mp="xglj9mnt3e2h">
                  Parameter
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-32" data-id="element-5903" data-mp="ngkydwaagpw6">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5904" data-mp="semj0lnd4x9r">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5905" data-mp="1g7csmo7sn4gv">
              {urinalysisTests.map(t => <tr key={t.name} data-id="element-5906" data-mp="1l3k04lfu8o0x">
                  <td className="border border-gray-300 p-1.5" data-id="element-5907" data-mp="1l5whoxhoowdg">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5908" data-mp="16exa9psl4dvg">
                    {lab.results![`UA_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5909" data-mp="1z3kzfwggfnea">
                    {t.normal || '—'}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isTestosteroneTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5910" data-mp="3kt4d6ecou4of">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5911" data-mp="3ks2tjlhlw9v3">
            Testosterone Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5912" data-mp="36v633l5egueq">
            <thead className="bg-gray-100" data-id="element-5913" data-mp="3bqd0ycbzn4vq">
              <tr data-id="element-5914" data-mp="2x30e8kag2t3r">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5915" data-mp="2x2iaz3pd7emc">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5916" data-mp="31tqkdzqp2zzi">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5917" data-mp="2o34wtxqaq1va">
                  Reference (M)
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5918" data-mp="2s4qedwdrz4bt">
                  Reference (F)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5919" data-mp="2e68wifajwm1t">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5920" data-mp="1ziia9bpx7xxv">
              {testosteroneTests.map(t => <tr key={t.name} data-id="element-5921" data-mp="1uim8lj4p4ilh">
                  <td className="border border-gray-300 p-1.5" data-id="element-5922" data-mp="1ujnvckv2qtj1">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5923" data-mp="29g69aduk7rlu">
                    {lab.results![`TESTO_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5924" data-mp="24imv1dxv3etw">
                    {t.rangeMale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5925" data-mp="2dhhy4h5m08sz">
                    {t.rangeFemale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5926" data-mp="2s535in9iou6p">
                    {t.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isAMHTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5927" data-mp="2x5r1zogim38b">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5928" data-mp="2x6sfdzl071t7">
            Anti Mullerian Hormone (AMH) Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5929" data-mp="397idztsf84w5">
            <thead className="bg-gray-100" data-id="element-5930" data-mp="397s2fcxti3zq">
              <tr data-id="element-5931" data-mp="3e61zxu70lgoi">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5932" data-mp="3e5k6147trejr">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5933" data-mp="3ixttmdlgw2iv">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5934" data-mp="3mxvlxw5bbjlh">
                  Reference (by age)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5935" data-mp="3rw5iaa2rwq8o">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5936" data-mp="5r1u2rerjeuv">
              <tr className="align-top" data-id="element-5937" data-mp="5pgv8plf19r7">
                <td className="border border-gray-300 p-1.5 font-medium" data-id="element-5938" data-mp="22j1yvu0umj6e">
                  {amhStructure.name}
                </td>
                <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5939" data-mp="27hdaa96sl1qq">
                  {lab.results![amhStructure.storageKey] || focusedTest && lab.results![focusedTest] || lab.results!['AMH'] || lab.results!['Anti-Müllerian Hormone'] || lab.results!['ANTI-MÜLLERIAN HORMONE'] || lab.results!['Anti Mullerian Hormone (AMH)'] || '—'}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5940" data-mp="17lmnazhl4iqe">
                  {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-5941" data-mp="13lcl7xwwycgt">
                      <span className="font-medium" data-id="element-5942" data-mp="13i6wwkw3zem5">{a.ageBand}:</span>{' '}
                      {a.range}
                    </div>)}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5943" data-mp="tlln7m959fbz">
                  {amhStructure.unit}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[10px] italic text-gray-600" data-id="element-5944" data-mp="3ebazbrigqn0z">
            Method: {amhStructure.method}
          </p>
        </div>;
    }
    if (isHormonalProfileTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5945" data-mp="1avxp6kzsv4id">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5946" data-mp="1aw7cu1xdgzkw">
            Hormonal Profile Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5947" data-mp="121j7ycvpljwm">
            <thead className="bg-gray-100" data-id="element-5948" data-mp="11zoshe37ijzx">
              <tr data-id="element-5949" data-mp="1psosh5clh0d9">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5950" data-mp="1ps56ndjtprty">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5951" data-mp="1ts7bfvkuv1dg">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5952" data-mp="1ft7memtfkkgr">
                  Reference Range
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5953" data-mp="1kuddkg7fzzo6">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5954" data-mp="9e6nq9d0to6h">
              {hormonalProfileTests.map(h => <tr key={h.key} data-id="element-5955" data-mp="1t34bwus7hs4">
                  <td className="border border-gray-300 p-1.5 font-medium" data-id="element-5956" data-mp="1sb8qm16pk1u">
                    {h.name}
                  </td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5957" data-mp="3t2i96p9chc9l">
                    {lab.results![`HP_${h.key}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5958" data-mp="5wiwafvwotyh">
                    {h.ranges.map(r => <div key={r.label} data-id="element-5959" data-mp="3ebszxsfg278d">
                        <span className="font-medium" data-id="element-5960" data-mp="3a9xwaha1jhrf">{r.label}:</span>{' '}
                        {r.value}
                      </div>)}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5961" data-mp="3o2xcymlk7i0k">
                    {h.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    // Fallback: generic key/value table
    return <div className="text-sm" data-id="element-5962" data-mp="2qlf21la6he5v">
        <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5963" data-mp="uik1rwjunb34">
          {activeTests.join(', ')} Report
        </h4>
        <table className="w-full text-xs border border-gray-300" data-id="element-5964" data-mp="zgvcsalvrrmx">
          <thead className="bg-gray-100" data-id="element-5965" data-mp="148ddcxjzrq7d">
            <tr data-id="element-5966" data-mp="149etvhjs14wh">
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-5967" data-mp="148vgme52n4os">
                Parameter
              </th>
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-5968" data-mp="199j78ype1jih">Result</th>
            </tr>
          </thead>
          <tbody data-id="element-5969" data-mp="bm0yhnvq8heh">
            {Object.entries(lab.results).map(([k, v]) => v ? <tr key={k} data-id="element-5970" data-mp="gkm09rqmn951">
                  <td className="border border-gray-300 p-1.5" data-id="element-5971" data-mp="lhm9gbirws0d">{k}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5972" data-mp="pjh8f9d8etaz">
                    {v}
                  </td>
                </tr> : null)}
          </tbody>
        </table>
      </div>;
  };
  const renderRadReport = (r: RadiologyRequest) => <div className="text-sm" data-id="element-5973" data-mp="210pdzxzm6fgq">
      <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5974" data-mp="3lgf1xkfbgntn">
        Radiology Report — {r.scanType}
      </h4>
      {r.completedAt && <p className="text-xs text-gray-500 mb-2" data-id="element-5975" data-mp="3hfv6qmeazua5">
          Completed: {new Date(r.completedAt).toLocaleString()}
        </p>}
      <div className="bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap text-sm" data-id="element-5976" data-mp="37p9xm51077cz">
        {r.report || 'No report available.'}
      </div>
      {r.images && r.images.length > 0 && <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-5977" data-mp="2xr2lbzhntdhb">
          {r.images.map((img, idx) => <img key={idx} src={img} alt={`${r.scanType} ${idx + 1}`} className="rounded border border-gray-200 object-cover w-full max-h-64" data-id="element-5978" data-mp="2tphd4rg2lndg" data-mpi={idx} />)}
        </div>}
    </div>;
  return <>
      <Card data-id="element-5979" data-mp="rw1jrmsllues">
        <div className="flex justify-between items-center mb-4 print:hidden" data-id="element-5980" data-mp="rvrq9p8ch54c">
          <div className="flex items-center gap-2 flex-1 max-w-md" data-id="element-5981" data-mp="mzt1w1n0nn3x">
            <div className="relative w-full" data-id="element-5982" data-mp="3s04g03qmy7vi">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5983" data-mp="3s1fim08ch9l1">
                <Search className="h-4 w-4 text-gray-400" data-id="element-5984" data-mp="ul69686wion" />
              </div>
              <input type="text" aria-label="Search laboratory and radiology reports" placeholder="Search patient name, BMC or report no..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-5985" data-mp="3i901140ymn0o" />
            </div>
          </div>
          <Badge variant="info" className="text-sm" data-id="element-5986" data-mp="34bbpimss8s8a">
            {filtered.length} report{filtered.length === 1 ? '' : 's'}
          </Badge>
        </div>

        {filtered.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-5987" data-mp="12g1ag1m7t34h">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-3" data-id="element-5988" data-mp="2n17rxjngzjv5" />
            <p className="text-lg font-medium text-gray-900" data-id="element-5989" data-mp="2i1lfhcfgu7ne">No reports yet</p>
            <p className="text-sm" data-id="element-5990" data-mp="2wpzflflhzi09">
              Completed lab and radiology reports will appear here.
            </p>
          </div> : <ul className="divide-y divide-gray-200" data-id="element-5991" data-mp="2ryhe8qfkbfer">
            {filtered.map(enc => {
          const patient = getPatient(enc.patientId);
          if (!patient) return null;
          const completedLabs = enc.labRequests.filter(l => l.status === 'Completed');
          const completedRads = enc.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0);
          return <li key={enc.id} className="py-4 flex justify-between items-start gap-4" data-id="element-5992" data-mp="1lbu8wetwgu18">
                  <div className="flex-1" data-id="element-5993" data-mp="1gdstc3voncui">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-5994" data-mp="39g9o2ls1gyxr">
                      <p className="text-base font-bold text-gray-900" data-id="element-5995" data-mp="34hyco6m3igdf">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <Badge variant="info" data-id="element-5996" data-mp="3j6behk7v6tfu">BMC: {patient.bmcFileNumber}</Badge>
                      <span className="text-xs text-gray-500" data-id="element-5997" data-mp="3e8t3zpe7865q">
                        {patient.age} yrs · {patient.sex}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-5998" data-mp="2ljm1ygfjcyfq">
                      Visit: {new Date(enc.startedAt).toLocaleString()}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-5999" data-mp="30ea71th0xuut">
                      {completedLabs.length > 0 && <span className="inline-flex items-center text-xs bg-yellow-50 text-yellow-800 px-2 py-1 rounded border border-yellow-200" data-id="element-6000" data-mp="fq46x2mbwc1s">
                          <FlaskConical className="w-3 h-3 mr-1" data-id="element-6001" data-mp="aq9j57xuo2ky" />
                          {completedLabs.length} Lab Report
                          {completedLabs.length > 1 ? 's' : ''}
                        </span>}
                      {completedRads.length > 0 && <span className="inline-flex items-center text-xs bg-purple-50 text-purple-800 px-2 py-1 rounded border border-purple-200" data-id="element-6002" data-mp="9b72p1dbd7im">
                          <Scan className="w-3 h-3 mr-1" data-id="element-6003" data-mp="1111sez532z7t" />
                          {completedRads.length} Radiology Report
                          {completedRads.length > 1 ? 's' : ''}
                        </span>}
                    </div>
                    <p className="mt-1.5 break-all font-mono text-[11px] text-gray-500" data-id="element-6004" data-mp="s8hr1qxeqgja">
                      {[...completedLabs.map(l => reportNumber('LAB', l.id)), ...completedRads.map(r => reportNumber('RAD', r.id))].join(' · ')}
                    </p>
                  </div>
                  <Button variant="outline" onClick={() => setViewing({
              encounter: enc
            })} data-id="element-6005" data-mp="1ymwbbxoeztwo">
                    <FileText className="w-4 h-4 mr-2" data-id="element-6006" data-mp="1pk3l7xqyxo8f" /> View Report
                  </Button>
                </li>;
        })}
          </ul>}
      </Card>

      {viewing && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-6007" data-mp="1fp1vp0ss6k5b">
          <div className="min-h-full flex items-start justify-center p-4" data-id="element-6008" data-mp="1avh7u1kwj6ju">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-6009" data-mp="16wi4gletjhui">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white z-10 print:hidden" data-id="element-6010" data-mp="11xx3gjrqsu50">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-6011" data-mp="rxo2ytqpwmjt">
                  Patient Report
                </h3>
                <div className="flex items-center gap-2" data-id="element-6012" data-mp="ht7p25wstfin">
                  <Button variant="outline" onClick={handlePrint} data-id="element-6013" data-mp="mtc3lqe5c068">
                    <Printer className="w-4 h-4 mr-2" data-id="element-6014" data-mp="mrhkmhmfqi4s" /> Print
                  </Button>
                  <Button variant="outline" onClick={handleDownloadPDF} data-id="element-6015" data-mp="rsyjy8p9twj9">
                    <Download className="w-4 h-4 mr-2" data-id="element-6016" data-mp="wrruptd6b9fq" /> Download PDF
                  </Button>
                  <button onClick={() => setViewing(null)} data-id="element-6017" data-mp="3v9x7wtvu2530">
                    <X className="w-5 h-5 text-gray-400" data-id="element-6018" data-mp="3v7b0cu8xzpks" />
                  </button>
                </div>
              </div>
              <div id="report-printable" className="p-6 overflow-y-auto flex-1" data-id="element-6019" data-mp="91e487a8lin9">
                {(() => {
              const patient = getPatient(viewing.encounter.patientId);
              if (!patient) return null;
              return <>
                      <div className="border-b-2 border-black pb-3 mb-4" data-id="element-6020" data-mp="3hhr2a1qb3ght">
                        <h2 className="text-xl font-bold uppercase" data-id="element-6021" data-mp="3hfmpsvecd95x">
                          Patient Report
                        </h2>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm" data-id="element-6022" data-mp="1i2xvfofixe04">
                          <p data-id="element-6023" data-mp="1wrbvjrlk2ocz">
                            <span className="font-semibold" data-id="element-6024" data-mp="1wrlmbhefczjq">Patient:</span>{' '}
                            {patient.firstName} {patient.lastName}
                          </p>
                          <p data-id="element-6025" data-mp="25rqjyzttgjkp">
                            <span className="font-semibold" data-id="element-6026" data-mp="25qyzol5o689q">BMC #:</span>{' '}
                            {patient.bmcFileNumber}
                          </p>
                          <p data-id="element-6027" data-mp="2fq6ap1oxlqto">
                            <span className="font-semibold" data-id="element-6028" data-mp="2fqg5d2kx8m5p">Age:</span>{' '}
                            {patient.age} yrs
                          </p>
                          <p data-id="element-6029" data-mp="2ap0fmx7strgz">
                            <span className="font-semibold" data-id="element-6030" data-mp="lbx413c48op9">Sex:</span>{' '}
                            {patient.sex}
                          </p>
                          <p className="col-span-2" data-id="element-6031" data-mp="gen3osn6usmn">
                            <span className="font-semibold" data-id="element-6032" data-mp="2rzvzgkktjl7q">Visit date:</span>{' '}
                            {new Date(viewing.encounter.startedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').map(lab => <div key={lab.id} className="mb-6" data-id="element-6033" data-mp="2e90ydugka8ep">
                            {renderLabResults(lab)}
                            {lab.labComments && <div className="mt-4 border border-gray-300 bg-gray-50 rounded p-3" data-id="element-6034" data-mp="1zdbb7szn3p8e">
                                <h5 className="font-bold uppercase mb-1 text-xs" data-id="element-6035" data-mp="24bczcsbxaeqq">
                                  Comments / Observations
                                </h5>
                                <pre className="whitespace-pre-wrap font-sans text-xs text-gray-800" data-id="element-6036" data-mp="2if4ys2hze3o">
                                  {lab.labComments}
                                </pre>
                              </div>}
                          </div>)}

                      {viewing.encounter.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0).map(r => <div key={r.id} className="mb-6" data-id="element-6037" data-mp="etpmbv152hcy">
                            {renderRadReport(r)}
                          </div>)}

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').length === 0 && viewing.encounter.radiologyRequests.filter(r => r.report).length === 0 && <p className="text-gray-500 text-center py-8" data-id="element-6038" data-mp="xqh39xt7kkdu">
                            No reports available for this encounter.
                          </p>}
                    </>;
            })()}
              </div>
            </div>
          </div>
        </div>}

      {/* Print-specific styling */}
      <style data-id="element-6039" data-mp="2ug5w7mlsy1cq">{`
        @media print {
          body * { visibility: hidden; }
          #report-printable, #report-printable * { visibility: visible; }
          #report-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
        }
      `}</style>
    </>;
};